import { useState, useEffect } from 'react'
import { Button, Loading, Empty, Tag } from 'react-vant'
import {
    Search, CalendarO, Play, Exchange, MedalO,
    ChartTrendingO, Arrow, ServiceO, Description, ShieldO
} from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import request from '../../api/request'

export default function StudentSportsHome() {
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState(0) // 0: 体质健康管理, 1: 运动会与班级赛
    const [children, setChildren] = useState([])
    const [selectedChild, setSelectedChild] = useState(null)
    const [latestTest, setLatestTest] = useState(null)
    const [sportsMeet, setSportsMeet] = useState(null)
    const [matches, setMatches] = useState([])

    // 获取绑定的学生信息
    useEffect(() => {
        const init = async () => {
            setLoading(true)
            try {
                const res = await request.get('/academic/students/m/children/')
                if (res.success && res.data && res.data.length > 0) {
                    setChildren(res.data)
                    setSelectedChild(res.data[0])
                }
            } catch (e) {
                console.error('获取学生信息失败:', e)
            } finally {
                setLoading(false)
            }
        }
        init()
    }, [])

    // 获取体测数据和运动会信息
    useEffect(() => {
        if (!selectedChild) return

        const fetchData = async () => {
            try {
                const yearRes = await request.get('/academic-year/current/')
                const currentYear = yearRes.current_academic_year

                // 体测数据
                const testRes = await request.get('/sports/tests/', {
                    params: { student_id: selectedChild.id, academic_year: currentYear, page_size: 1 }
                })
                if (testRes.items && testRes.items.length > 0) {
                    setLatestTest(testRes.items[0])
                } else {
                    setLatestTest(null)
                }
            } catch (e) {
                console.error('获取数据失败:', e)
            }
        }

        const fetchMeet = async () => {
            try {
                const res = await request.get('/sports/dashboard/overview/', { params: { sports_meet_id: 'current' } })
                if (res && res.sports_meet_name) {
                    setSportsMeet(res)
                }
            } catch (e) { /* 无运动会也能正常呈现 */ }
        }

        const fetchMatches = async () => {
            try {
                const res = await request.get('/sports/class-match/', { params: { limit: 5 } })
                setMatches(res || [])
            } catch (e) { /* ignore */ }
        }

        fetchData()
        fetchMeet()
        fetchMatches()
    }, [selectedChild])

    // 直达学生个人成绩单或历史查询
    const handleScoreQuery = () => {
        if (latestTest?.id) {
            navigate(`/student/physical-test/detail/${latestTest.id}`)
        } else {
            navigate('/student/physical-test/query')
        }
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--canvas-bg, #f8fafc)' }}>
                <Loading type="spinner" color="var(--primary-color, #056b4f)" vertical>加载中...</Loading>
            </div>
        )
    }

    const isParent = userInfo?.role === 'parent'
    const studentClass = selectedChild?.class_name || userInfo?.class_name || '1班'

    return (
        <div style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 90 }}>
            {/* ========== 顶部 Hero Header (App 风格青蓝渐变) ========== */}
            <div style={{
                background: 'var(--primary-gradient, linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%))',
                padding: '44px 20px 48px',
                position: 'relative',
                overflow: 'hidden',
                color: '#fff'
            }}>
                {/* 装饰半透圆 */}
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />
                <div style={{
                    position: 'absolute', bottom: -10, left: -20,
                    width: 90, height: 90, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.05)'
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                    <h2 style={{
                        margin: 0, fontSize: 24, fontWeight: 800, color: '#fff',
                        letterSpacing: '-0.3px'
                    }}>
                        校园体育
                    </h2>
                    <p style={{
                        margin: '6px 0 0',
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: 13,
                        fontWeight: 400
                    }}>
                        体质健康监测与赛事竞技中心
                    </p>
                </div>
            </div>

            {/* ========== 双轨分段切换器 (Segmented Tabs) ========== */}
            <div style={{ padding: '0 16px', marginTop: -26, position: 'relative', zIndex: 10 }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 16,
                    padding: 4,
                    display: 'flex',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                    border: '1px solid var(--border-subtle, #e2e8f0)'
                }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab(0)}
                        style={{
                            flex: 1,
                            padding: '10px 0',
                            border: 'none',
                            borderRadius: 12,
                            background: activeTab === 0 ? '#f0fdf4' : 'transparent',
                            color: activeTab === 0 ? 'var(--primary-color, #056b4f)' : 'var(--text-secondary, #475569)',
                            fontWeight: activeTab === 0 ? 700 : 500,
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <span>💙</span>
                        <span>体质健康管理</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab(1)}
                        style={{
                            flex: 1,
                            padding: '10px 0',
                            border: 'none',
                            borderRadius: 12,
                            background: activeTab === 1 ? '#fff7ed' : 'transparent',
                            color: activeTab === 1 ? 'var(--athletic-color, #ff5e1e)' : 'var(--text-secondary, #475569)',
                            fontWeight: activeTab === 1 ? 700 : 500,
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <span>🏆</span>
                        <span>运动会与班级赛</span>
                    </button>
                </div>
            </div>

            {/* ========== 轨道 1：学生体质健康管理 (契合 Image 10) ========== */}
            {activeTab === 0 && (
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 1. 悬浮个人健康概览卡 (三列布局: 就读班级 | 当前身份 | 体测档案) */}
                    <div
                        onClick={handleScoreQuery}
                        style={{
                            background: 'var(--card-bg, #ffffff)',
                            borderRadius: 16,
                            padding: '16px 20px',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)', marginBottom: 4 }}>就读班级</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary-color, #056b4f)' }}>
                                {studentClass}
                            </div>
                        </div>

                        <div style={{
                            textAlign: 'center',
                            borderLeft: '1px solid #f1f5f9',
                            borderRight: '1px solid #f1f5f9'
                        }}>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)', marginBottom: 4 }}>当前身份</div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--emerald-color, #00b578)' }}>
                                {isParent ? '学生家长' : '在校学生'}
                            </div>
                        </div>

                        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)', marginBottom: 4 }}>体测档案</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 700, color: latestTest ? '#00b578' : '#64748b' }}>
                                    {latestTest ? '已建档' : '未测'}
                                </span>
                                <Arrow style={{ fontSize: 12, color: '#94a3b8' }} />
                            </div>
                        </div>
                    </div>

                    {/* 2. 核心 2x2 动作卡片通道 */}
                    <div>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            marginBottom: 10, padding: '0 4px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                    学生体质与健康成长
                                </span>
                            </div>
                            <span
                                onClick={() => navigate('/student/physical-test/standards')}
                                style={{ fontSize: 12, color: 'var(--primary-color, #056b4f)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                            >
                                2014版标准 <Arrow style={{ fontSize: 10 }} />
                            </span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                            {/* 卡片 1: 成绩查询 (极速首选/我的成绩) */}
                            <div
                                onClick={handleScoreQuery}
                                style={{
                                    background: 'var(--card-bg, #ffffff)',
                                    borderRadius: 16,
                                    padding: '16px',
                                    border: '1px solid var(--border-subtle, #e2e8f0)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: 124
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: 10, right: 10,
                                    background: '#ffefe6', color: '#ff5e1e',
                                    fontSize: 10, fontWeight: 600,
                                    padding: '2px 8px', borderRadius: 10
                                }}>
                                    我的成绩
                                </div>
                                <div style={{
                                    width: 44, height: 44, borderRadius: 12,
                                    background: '#f0fdf4',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'var(--primary-color, #056b4f)',
                                    marginBottom: 12
                                }}>
                                    <MedalO style={{ fontSize: 24 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>成绩查询</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                        当年与历年单项明细 · 电子卡
                                    </div>
                                </div>
                            </div>

                            {/* 卡片 2: 成长档案 */}
                            <div
                                onClick={() => navigate('/student/physical-test/portfolio')}
                                style={{
                                    background: 'var(--card-bg, #ffffff)',
                                    borderRadius: 16,
                                    padding: '16px',
                                    border: '1px solid var(--border-subtle, #e2e8f0)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: 124
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: 10, right: 10,
                                    background: '#e8f6f0', color: '#056b4f',
                                    fontSize: 10, fontWeight: 600,
                                    padding: '2px 8px', borderRadius: 10
                                }}>
                                    成长轨迹
                                </div>
                                <div style={{
                                    width: 44, height: 44, borderRadius: 12,
                                    background: '#f0fdf4',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'var(--primary-color, #056b4f)',
                                    marginBottom: 12
                                }}>
                                    <ChartTrendingO style={{ fontSize: 24 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>成长档案</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                        身高体重 BMI · 体质变化趋势
                                    </div>
                                </div>
                            </div>

                            {/* 卡片 3: 免测申请 */}
                            <div
                                onClick={() => navigate('/student/physical-test/exemption')}
                                style={{
                                    background: 'var(--card-bg, #ffffff)',
                                    borderRadius: 16,
                                    padding: '16px',
                                    border: '1px solid var(--border-subtle, #e2e8f0)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: 124
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: 10, right: 10,
                                    background: '#ecfdf5', color: '#00b578',
                                    fontSize: 10, fontWeight: 600,
                                    padding: '2px 8px', borderRadius: 10
                                }}>
                                    在线申报
                                </div>
                                <div style={{
                                    width: 44, height: 44, borderRadius: 12,
                                    background: '#ecfdf5',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'var(--emerald-color, #00b578)',
                                    marginBottom: 12
                                }}>
                                    <ShieldO style={{ fontSize: 24 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>免测申请</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                        因病/残免测在线申请 · 材料提交
                                    </div>
                                </div>
                            </div>

                            {/* 卡片 4: 国家标准 */}
                            <div
                                onClick={() => navigate('/student/physical-test/standards')}
                                style={{
                                    background: 'var(--card-bg, #ffffff)',
                                    borderRadius: 16,
                                    padding: '16px',
                                    border: '1px solid var(--border-subtle, #e2e8f0)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    minHeight: 124
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: 10, right: 10,
                                    background: '#f5f3ff', color: '#7c3aed',
                                    fontSize: 10, fontWeight: 600,
                                    padding: '2px 8px', borderRadius: 10
                                }}>
                                    标准库
                                </div>
                                <div style={{
                                    width: 44, height: 44, borderRadius: 12,
                                    background: '#f5f3ff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#7c3aed',
                                    marginBottom: 12
                                }}>
                                    <Description style={{ fontSize: 24 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>国家标准</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                        2014版教育部中小学生评分标准
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. 体测工具箱 (契合 Image 10) */}
                    <div>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            marginBottom: 10, padding: '0 4px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                    体测工具箱
                                </span>
                                <span style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)' }}>
                                    自测计算与评分对照
                                </span>
                            </div>
                            <span
                                onClick={() => navigate('/student/physical-test/calculator')}
                                style={{ fontSize: 12, color: 'var(--primary-color, #056b4f)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}
                            >
                                计算器 <Arrow style={{ fontSize: 10 }} />
                            </span>
                        </div>

                        <div style={{
                            background: 'var(--card-bg, #ffffff)',
                            borderRadius: 16,
                            padding: '16px',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 12
                        }}>
                            <div
                                onClick={() => navigate('/student/physical-test/calculator')}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    padding: '12px 8px', borderRadius: 12, background: '#f8fafc',
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{ fontSize: 28, marginBottom: 6 }}>🧮</div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
                                    体测计算器
                                </span>
                            </div>

                            <div
                                onClick={() => navigate('/student/physical-test/exemption')}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    padding: '12px 8px', borderRadius: 12, background: '#f8fafc',
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%', background: '#ecfdf5',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#00b578', marginBottom: 6
                                }}>
                                    <ShieldO style={{ fontSize: 20 }} />
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
                                    免测申请
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== 轨道 2：运动会与班级赛 (契合 Image 7) ========== */}
            {activeTab === 1 && (
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 1. 班级趣味对抗赛专卡 (Image 7 风格) */}
                    <div
                        onClick={() => navigate('/student/sports/class-match')}
                        style={{
                            background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                            borderRadius: 18,
                            padding: '18px 20px',
                            color: '#fff',
                            cursor: 'pointer',
                            boxShadow: '0 8px 24px rgba(5, 107, 79, 0.25)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{
                            position: 'absolute', right: -15, bottom: -15,
                            fontSize: 72, opacity: 0.15, pointerEvents: 'none'
                        }}>
                            ⚽
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>CAMPUS LEAGUE</div>
                                <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>🏆 班级趣味对抗赛</div>
                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                                    世界杯月 · 积分对抗 · 巅峰决赛
                                </div>
                            </div>
                            <div style={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 12,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                赛程与比分 <Arrow style={{ fontSize: 10 }} />
                            </div>
                        </div>
                    </div>

                    {/* 2. 运动会赛事大盘 / 榜单 */}
                    {sportsMeet && sportsMeet.status !== 'error' && (
                        <div style={{
                            background: 'var(--card-bg, #ffffff)',
                            borderRadius: 18,
                            padding: '20px',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <div>
                                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', fontWeight: 600 }}>🏟️ 校园赛事</div>
                                    <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                        {sportsMeet.sports_meet_name}
                                    </div>
                                </div>
                                <Tag
                                    type={sportsMeet.status === 'ongoing' ? 'success' : 'primary'}
                                    style={{
                                        background: sportsMeet.status === 'ongoing' ? '#ecfdf5' : '#f0fdf4',
                                        border: 'none',
                                        color: sportsMeet.status === 'ongoing' ? '#00b578' : '#056b4f',
                                        fontWeight: 600, fontSize: 11,
                                        borderRadius: 8, padding: '2px 8px'
                                    }}
                                >
                                    {sportsMeet.status === 'ongoing' ? '进行中' : '筹备中'}
                                </Tag>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                                {[
                                    { label: '比赛项目', value: sportsMeet.total_events || 0 },
                                    { label: '参赛人数', value: sportsMeet.total_participants || 0 },
                                    { label: '完赛进度', value: `${sportsMeet.progress_percentage || 0}%` },
                                ].map((item, idx) => (
                                    <div key={idx} style={{
                                        background: '#f8fafc',
                                        borderRadius: 12, padding: '10px 8px',
                                        textAlign: 'center',
                                        border: '1px solid #f1f5f9'
                                    }}>
                                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>{item.value}</div>
                                        <div style={{ fontSize: 10, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>{item.label}</div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: 10 }}>
                                <Button
                                    round size="small"
                                    style={{
                                        flex: 1, height: 36,
                                        background: '#ecfdf5', border: '1px solid #a7f3d0',
                                        color: '#00b578', fontWeight: 600, fontSize: 13
                                    }}
                                    icon={<Exchange style={{ fontSize: 14 }} />}
                                    onClick={() => navigate('/student/sports/rank')}
                                >
                                    实时榜单
                                </Button>
                                <Button
                                    round size="small"
                                    style={{
                                        flex: 1, height: 36,
                                        background: '#f0fdf4', border: '1px solid #a7f3d0',
                                        color: '#056b4f', fontWeight: 600, fontSize: 13
                                    }}
                                    icon={<CalendarO style={{ fontSize: 14 }} />}
                                    onClick={() => navigate('/student/sports/schedule')}
                                >
                                    比赛日程
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* 3. 班级赛列表 */}
                    <div style={{
                        background: 'var(--card-bg, #ffffff)',
                        borderRadius: 18,
                        padding: '18px',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 4, height: 15, background: 'var(--athletic-color, #ff5e1e)', borderRadius: 2 }} />
                                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                    进行中的比赛
                                </span>
                            </div>
                            <span
                                style={{ fontSize: 12, color: 'var(--primary-color, #056b4f)', fontWeight: 600, cursor: 'pointer' }}
                                onClick={() => navigate('/student/sports/class-match')}
                            >
                                全部赛事 <Arrow style={{ fontSize: 10 }} />
                            </span>
                        </div>

                        {matches.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {matches.slice(0, 3).map((match, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => navigate(`/student/sports/class-match/${match.id}`)}
                                        style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '12px 14px',
                                            borderRadius: 12,
                                            background: '#f8fafc',
                                            border: '1px solid #f1f5f9',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{match.title}</div>
                                            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                                📍 {match.venue || '学校操场'} · {match.match_date}
                                            </div>
                                        </div>
                                        <Tag
                                            type={match.status === 'ongoing' ? 'success' : 'primary'}
                                            style={{
                                                borderRadius: 8,
                                                fontWeight: 600,
                                                fontSize: 10,
                                                background: match.status === 'ongoing' ? '#ecfdf5' : '#f0fdf4',
                                                border: 'none',
                                                color: match.status === 'ongoing' ? '#00b578' : '#056b4f',
                                                padding: '2px 8px'
                                            }}
                                        >
                                            {match.status === 'ongoing' ? '进行中' : match.status === 'completed' ? '已结束' : '待开始'}
                                        </Tag>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-tertiary, #94a3b8)' }}>
                                <div style={{ fontSize: 32, marginBottom: 6 }}>⚽</div>
                                <div style={{ fontSize: 12 }}>暂无进行中的班级赛事</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
